import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Siren, CalendarX, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from "recharts";

function Delta({ value }) {
  if (value === 0) return (
    <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
      <Minus className="w-3 h-3" /> ثابت
    </span>
  );
  const up = value > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${up ? "text-rose-700 bg-rose-100" : "text-emerald-700 bg-emerald-100"}`}>
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {up ? "+" : ""}{value}
    </span>
  );
}

function Sparkline({ data, dataKey, color, testid }) {
  const max = Math.max(1, ...data.map((d) => d[dataKey] || 0));
  return (
    <div className="h-14 -mx-1" dir="ltr" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 4, left: 4, bottom: 0 }} barCategoryGap={4}>
          <XAxis dataKey="day" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} />
          <Tooltip
            cursor={{ fill: "rgba(148,163,184,0.08)" }}
            contentStyle={{ borderRadius: 10, borderColor: "#E2E8F0", fontSize: 12, padding: "6px 10px" }}
            formatter={(v) => [v, dataKey === "emergencies" ? "طوارئ" : "غياب"]}
            labelFormatter={(l) => l}
          />
          <Bar dataKey={dataKey} radius={[4, 4, 0, 0]} minPointSize={2}>
            {data.map((d, i) => (
              <Cell key={i} fill={d[dataKey] === max && max > 0 ? color : `${color}80`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Metric({ icon: Icon, label, data, hue = "sky", daily, dailyKey, sparkColor, testidPrefix }) {
  const HUE = {
    sky: "bg-sky-100 text-sky-700",
    rose: "bg-rose-100 text-rose-700",
    amber: "bg-amber-100 text-amber-700",
  }[hue];
  return (
    <div className="flex flex-col gap-2 flex-1 min-w-[260px]" data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl ${HUE} flex items-center justify-center shrink-0`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-3xl font-black text-slate-900">{data.current}</span>
            <span className="text-xs text-slate-500">هذا الأسبوع</span>
            <Delta value={data.delta} />
          </div>
          <div className="text-xs text-slate-400 mt-0.5">الأسبوع السابق: {data.previous}</div>
        </div>
      </div>
      {daily && daily.length > 0 && (
        <Sparkline data={daily} dataKey={dailyKey} color={sparkColor} testid={`${testidPrefix}-sparkline`} />
      )}
    </div>
  );
}

export default function WeeklyComparisonWidget() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/stats/weekly-comparison").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  return (
    <Card className="border-slate-200/70 card-elevated" data-testid="weekly-comparison">
      <CardContent className="p-5 flex items-stretch gap-6 flex-wrap">
        <Metric icon={Siren} label="الطوارئ" data={data.emergencies} hue="rose"
                daily={data.daily} dailyKey="emergencies" sparkColor="#E11D48" testidPrefix="emergencies" />
        <div className="w-px bg-slate-200 hidden lg:block" />
        <Metric icon={CalendarX} label="الغياب" data={data.absences} hue="amber"
                daily={data.daily} dailyKey="absences" sparkColor="#D97706" testidPrefix="absences" />
      </CardContent>
    </Card>
  );
}
